import ArchiveIcon from '@mui/icons-material/Archive';
import EditIcon from '@mui/icons-material/Edit';
import MessageIcon from '@mui/icons-material/Message';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Button } from 'src/UIKit/components';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useArchiveAppointmentTypeApi,
  useGetAppointmentTypeApi,
  useGetAppointmentTypesApi,
  useRestoreAppointmentTypeApi,
} from 'src/modules/calendar/api';
import { GetAppointmentTypesParams } from 'src/modules/calendar/types';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import GridData from 'src/components/GridData/GridData';
import { TableHeader } from 'src/components/TableHeader/TableHeader';
import { AppointmentType } from 'src/constants/types';
import { isActionAllowed } from 'src/helpers/permissions';
import { getSortFieldForApi } from 'src/helpers/sort';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { Contact } from 'src/modules/contacts/types';
import { selectProfile } from 'src/modules/profile/slices';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import CreateAppointmentTypeModal from './CreateAppointmentTypeModal/view';
import { getRandomHexColor, settingsTabPanelSx } from 'src/modules/settings/helpers';
import { campaignBuilderPath } from 'src/modules/campaigns/helpers';

const AppointmentTypes = () => {
  const [page, setPage] = useState<number>(0);
  const [sortField, setSortField] = useState<string>('name_asc');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [appointmentTypes, setAppointmentTypes] = useState<{
    count: number;
    items: AppointmentType[] | null;
  }>({
    count: 0,
    items: null,
  });
  const [editableAppointmentType, setEditableAppointmentType] = useState<AppointmentType | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const { profile } = useSelector(selectProfile);
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const getAppointmentTypes = useGetAppointmentTypesApi();
  const getAppointmentType = useGetAppointmentTypeApi();
  const archiveAppointmentType = useArchiveAppointmentTypeApi();
  const restoreAppointmentType = useRestoreAppointmentTypeApi();
  const navigate = useNavigate();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const pageSize = 25;
  const isButtonsDisabled = !profile || profile?.role?.name === 'company_user';

  const initialAppointmentType: AppointmentType = useMemo(
    () => ({
      id: "",
      name: "",
      color: getRandomHexColor(),
      companyId: selectedCompanyId!,
      description: "",
      location: "",
      notification: "",
      campaignId: undefined,
      campaignName: undefined,
    }),
    [selectedCompanyId]
  );

  const handleAppointmentTypeEdit = (appointmentTypeId: string) => {
    if (selectedCompanyId) {
      getAppointmentType(appointmentTypeId, selectedCompanyId).then(response => {
        setEditableAppointmentType(response.data);
        setShowCreateModal(true);
      });
    }
  };

  const handleAppointmentTypeDelete = (appointmentTypeId: string) => {
    selectedCompanyId && archiveAppointmentType(appointmentTypeId, selectedCompanyId).then(refreshAppointmentTypes);
  };

  const handleAppointmentTypeRestore = (appointmentTypeId: string) => {
    selectedCompanyId && restoreAppointmentType(appointmentTypeId, selectedCompanyId).then(refreshAppointmentTypes);
  };

  const handleCampaignNavigation = (appointmentTypeId: string) => {
    if (selectedCompanyId) {
      getAppointmentType(appointmentTypeId, selectedCompanyId).then(response => {
        const appointmentType = response.data;
        const campaignId = appointmentType.campaignId;
        if (campaignId) {
          navigate(campaignBuilderPath(`/reminder/${campaignId}`));
        } else {
          navigate(campaignBuilderPath('/reminder'), {
            state: { appointmentTypeId: appointmentType.id },
          });
        }
      });
    }
  };

  const refreshAppointmentTypes = () => {
    setEditableAppointmentType(undefined);
    if (selectedCompanyId) {
      setIsLoading(true);
      const reqQuery: GetAppointmentTypesParams = {
        companyId: selectedCompanyId,
        limit: pageSize,
        offset: page * pageSize,
        sort: `${getSortFieldForApi(sortField)}`,
        statuses: 'active,archived',
      };
      if (searchQuery) {
        reqQuery.query = searchQuery;
      }
      getAppointmentTypes(reqQuery)
        .then(({ data }) => {
          setAppointmentTypes(data);
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    selectedCompanyId && refreshAppointmentTypes();
  }, [page, selectedCompanyId, sortField, searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [sortField, searchQuery]);

  const formatAssignee = (assignee: Contact): string => {
    if (assignee?.firstName || assignee?.lastName) {
      return `${assignee.firstName || ""} ${assignee.lastName || ""}`;
    }
    return assignee?.phoneNumber || "";
  };

  const formatTableRows = (appointmentTypes: AppointmentType[]) => {
    return appointmentTypes.map((item: AppointmentType) => {
      return {
        id: item.id,
        name: item.name,
        assignedTo: item.assignedTo,
        description: item.description,
        location: item.location,
        notification: item.notification,
        color: item.color,
        status: item.status,
        actions: 'edit/delete',
      };
    });
  };

  if (!profile) {
    return null;
  }

  return (
    <>
      <CreateAppointmentTypeModal
        initialAppointmentType={initialAppointmentType}
        appointmentType={editableAppointmentType || initialAppointmentType}
        show={showCreateModal}
        setShowModal={setShowCreateModal}
        refreshAppointmentTypes={refreshAppointmentTypes}
      />

      <Box sx={settingsTabPanelSx}>
        <GridData
          header={
            <TableHeader
              searchValue={searchQuery}
              onSearchChange={(_: ChangeEvent<HTMLInputElement>, query: string) => {
                setSearchQuery(query);
              }}
              actions={
                <Button
                  variant="contained"
                  onClick={() => {
                    setEditableAppointmentType(undefined);
                    setShowCreateModal(true);
                  }}
                  disabled={!isActionAllowed({ authorRole: profile?.role?.name })}
                >
                  Add New Calendar
                </Button>
              }
            />
          }
          isLoading={isLoading}
          rows={formatTableRows(appointmentTypes.items || [])}
          columns={[
            {
              field: 'name',
              headerName: 'Calendar',
              minWidth: 200,
              editable: false,
              flex: 1,
              renderCell: (params) => {
                return (
                  <Stack direction="column" height="100%" justifyContent="center">
                    <Typography
                      variant="body2"
                      noWrap
                    >
                      {params.value && (
                        <Box>
                          {params.value}{" "}
                          {params.row.status === "active" ? (
                            ""
                          ) : (
                            <Typography component="span" variant="body2" fontWeight="bold">(Archive)</Typography>
                          )}
                        </Box>
                      )}
                    </Typography>
                  </Stack>
                );
              },
            },
            {
              field: 'assignedTo',
              headerName: 'Assignee',
              minWidth: 150,
              editable: false,
              flex: 1,
              renderCell: (params) => {
                return (
                  <Stack direction="column" height="100%" justifyContent="center">
                    <Typography
                      variant="body2"
                      noWrap
                    >
                      {params.value && <Box>{formatAssignee(params.value as Contact)}</Box>}
                    </Typography>
                  </Stack>
                );
              },
            },
            {
              field: 'description',
              headerName: 'Description',
              minWidth: 200,
              editable: false,
              sortable: false,
              flex: 1,
            },
            {
              field: 'location',
              headerName: 'Location',
              minWidth: 150,
              editable: false,
              sortable: false,
              flex: 1,
            },
            {
              field: 'notification',
              headerName: 'Notification',
              minWidth: 150,
              editable: false,
              sortable: false,
              flex: 1,
              renderCell: (params) => {
                return <>{params.value?.replace("-", "")}</>;
              },
            },
            {
              field: 'color',
              headerName: 'Color',
              minWidth: 100,
              editable: false,
              sortable: false,
              flex: 1,
              renderCell: (params) => {
                return (
                  <Stack direction="column" height="100%" justifyContent="center">
                    <Typography
                      component="div"
                      variant="body2"
                      noWrap
                    >
                      {params.value && (
                        <Box display="inline-block" borderRadius={1} p={1} bgcolor={params.value}>
                          {params.value}
                        </Box>
                      )}
                    </Typography>
                  </Stack>
                );
              },
            },
          ]}
          renderAction={(row) => {
            return (
              <>
                <IconButton
                  disabled={isButtonsDisabled}
                  size="small"
                  color="primary"
                  onClick={() => handleAppointmentTypeEdit(row.id)}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                  disabled={isButtonsDisabled}
                  size="small"
                  color="primary"
                  onClick={() => handleCampaignNavigation(row.id)}
                >
                  <MessageIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                  disabled={isButtonsDisabled}
                  size="small"
                  color="primary"
                  onClick={() => {
                    const isArchived = row.status === 'archived';
                    const actionText = isArchived ? 'unarchive' : 'archive';
                    const actionName = isArchived ? 'Unarchive' : 'Archive';
                    
                    openDialog({
                      title: `${actionName} Calendar`,
                      content: `Are you sure you want to ${actionText} this calendar?`,
                      onConfirm: () => {
                        if (isArchived) {
                          handleAppointmentTypeRestore(row.id);
                        } else {
                          handleAppointmentTypeDelete(row.id);
                        }
                      },
                    });
                  }}
                >
                  {row.status === 'archived' ? (
                    <UnarchiveIcon fontSize="inherit" />
                  ) : (
                    <ArchiveIcon fontSize="inherit" />
                  )}
                </IconButton>
              </>
            );
          }}
          sorting={{
            value: sortField,
            onChange: (s: GridSortModel) => {
              if (s[0]) {
                setSortField(`${s[0]?.field}_${s[0]?.sort}`);
              }
            },
          }}
          pagination={{
            page: page,
            pageSize: pageSize,
            totalCount: appointmentTypes.count,
            onChange: (p: GridPaginationModel) => {
              setPage(p.page);
            },
          }}
        />
      </Box>
      <ConfirmationDialog />
    </>
  );
};

export default AppointmentTypes;
